// One sign-in across every F2 host (F2-PLAN-SSO-01, Phase 3).
//
// flex2-api's Auth:Mode decides, read from GET /auth/mode so QC and prod switch
// without a rebuild:
//   "password" — sign-in works exactly as before; nothing here runs.
//   "oidc"     — a finished sign-in answers { next }: a single-use Keycloak link.
//                The browser follows it (Keycloak starts its session) and lands on
//                /auth/callback, which gets this host's own tokens from Keycloak
//                (client f2-web, code + PKCE) and the sign-in details from GET
//                /auth/session. Any other F2 host started with no token asks
//                Keycloak the same way with prompt=none and is signed in without a
//                prompt.
//
// The access token still lives in localStorage.authToken, so every API caller is
// unchanged. Renewal goes straight to Keycloak (f2-web is a public client; see
// utils/session). Signing out ends the Keycloak session, so every other host is
// signed out at its next renewal.
//
// Network calls use fetch, not axios: the app's global axios config adds _subdomain
// and a stale bearer to every request (Keycloak's CORS check refuses the header) and
// signs the user out on a 401, neither of which may happen mid-sign-in.

// localStorage: { authority, clientId, idToken } while this host holds a Keycloak
// (single sign-on) session; absent for a password-mode session.
const SESSION_KEY = "f2.sso";
// sessionStorage: the sign-in request this tab sent to Keycloak.
const PENDING_KEY = "f2.sso.pending";
// localStorage: the last GET /auth/mode answer, so password mode renders at once.
const MODE_KEY = "f2.sso.mode";
// sessionStorage: who a followed session link was for, and its claim secret.
const EXPECT_KEY = "f2.sso.expect";
const CLAIM_KEY = "f2.sso.claim";
// sessionStorage: a message for the sign-in page to show once (takeSignInNotice).
const NOTICE_KEY = "f2.sso.notice";
// sessionStorage: when a prompt=none attempt last found no Keycloak session.
const SILENT_MISS_KEY = "f2.sso.silentMissAt";
const SILENT_MISS_QUIET_MS = 60 * 1000;

export const CALLBACK_PATH = "/auth/callback";

const API = process.env.REACT_APP_API_URL;

// fetch that gives up after timeoutMs; resolves the parsed JSON body ({} when none)
// with the status, rejects only when the server can't be reached.
export async function fetchJson(url, { timeoutMs = 20000, ...init } = {}) {
  const controller = typeof AbortController === "undefined" ? null : new AbortController();
  const timer = controller ? setTimeout(() => controller.abort(), timeoutMs) : null;
  try {
    const res = await fetch(url, { ...init, signal: controller?.signal });
    let body = {};
    try {
      body = await res.json();
    } catch (_e) {
      /* no JSON body */
    }
    return { ok: res.ok, status: res.status, body };
  } finally {
    if (timer) clearTimeout(timer);
  }
}

// Keycloak's token endpoint, form-encoded (a CORS-safelisted type: no preflight).
const tokenRequest = (authority, form) =>
  fetchJson(`${authority}/protocol/openid-connect/token`, { method: "POST", body: new URLSearchParams(form) });

// Pages that must not adopt a Keycloak session on their own: the callback itself,
// impersonation's landing (it signs in as someone else), and the pages that sign in
// with their own one-time token or need no sign-in.
const NO_SILENT_SIGN_IN =
  /^\/(auth\/callback|auth-check|set-password|login\/link|forgot-password|reset-password|signup|verify|afms\/driver)(\/|$)/;

const readJson = (storage, key) => {
  try {
    return JSON.parse(storage.getItem(key) || "null");
  } catch (_e) {
    return null;
  }
};
// False when the browser won't store it (storage disabled).
const writeJson = (storage, key, value) => {
  try {
    if (value == null) storage.removeItem(key);
    else storage.setItem(key, JSON.stringify(value));
    return true;
  } catch (_e) {
    return false;
  }
};

// ---- mode -------------------------------------------------------------------------

let modeRequest = null;

// { mode: "password" } or { mode: "oidc", authority, clientId }, asked once per page
// load and remembered. Anything but a clear "oidc" answer (an older API, a network
// blip) means password: sign in as before.
export function loadSignInMode() {
  if (!modeRequest) {
    modeRequest = fetchJson(`${API}/auth/mode`, { timeoutMs: 5000 })
      .then((res) => {
        const data = res.ok ? res.body?.data : null;
        const mode = data?.mode === "oidc" && data.authority && data.clientId ? data : { mode: "password" };
        writeJson(localStorage, MODE_KEY, mode);
        return mode;
      })
      .catch(() => ({ mode: "password" }));
  }
  return modeRequest;
}

// ---- this host's Keycloak session -------------------------------------------------

export const readSsoSession = () => readJson(localStorage, SESSION_KEY);
export const isSsoSession = () => Boolean(readSsoSession()?.authority);
export const storeSsoSession = (session) => writeJson(localStorage, SESSION_KEY, session);
export const clearSsoSession = () => writeJson(localStorage, SESSION_KEY, null);

// Keycloak's sign-out for this session, landing on this host's /login; null when
// the session isn't a single sign-on one. With the ID token as hint Keycloak signs
// out without asking.
export function endSessionUrl(session = readSsoSession()) {
  if (!session?.authority) return null;
  const params = new URLSearchParams({
    client_id: session.clientId,
    post_logout_redirect_uri: `${window.location.origin}/login`,
  });
  if (session.idToken) params.set("id_token_hint", session.idToken);
  return `${session.authority}/protocol/openid-connect/logout?${params}`;
}

// ---- PKCE ---------------------------------------------------------------------------

const base64Url = (bytes) =>
  btoa(String.fromCharCode(...new Uint8Array(bytes)))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");

const randomString = (bytes) => {
  const a = new Uint8Array(bytes);
  window.crypto.getRandomValues(a);
  return base64Url(a);
};

export const codeChallengeOf = async (verifier) =>
  base64Url(await window.crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier)));

// ---- signing in ---------------------------------------------------------------------

// This tab found no usable Keycloak session: for a minute, start-up shows the
// sign-in page instead of asking again (no redirect loop on reload).
export function noteSilentMiss() {
  try {
    sessionStorage.setItem(SILENT_MISS_KEY, String(Date.now()));
  } catch (_e) {
    /* ignore */
  }
}

// Follow a sign-in's { next, subject, claim }: Keycloak starts its session, then
// returns to /auth/callback. The page's postLoginRedirect (sessionStorage) survives the
// trip, and so do subject, which the callback checks (see readCallback), and claim, the
// one-time secret that binds the new Keycloak session to this sign-in (POST
// /auth/session). Without the claim no session gets in, so it never leaves this tab.
export function followSessionLink(next, subject, claim) {
  try {
    if (subject) sessionStorage.setItem(EXPECT_KEY, subject);
    else sessionStorage.removeItem(EXPECT_KEY);
    if (claim) sessionStorage.setItem(CLAIM_KEY, claim);
    else sessionStorage.removeItem(CLAIM_KEY);
  } catch (_e) {
    /* ignore */
  }
  window.location.assign(next);
}

// A message the sign-in page shows once (e.g. why it is asking again).
export function setSignInNotice(message) {
  try {
    sessionStorage.setItem(NOTICE_KEY, message);
  } catch (_e) {
    /* ignore */
  }
}

export function takeSignInNotice() {
  try {
    const message = sessionStorage.getItem(NOTICE_KEY);
    sessionStorage.removeItem(NOTICE_KEY);
    return message;
  } catch (_e) {
    return null;
  }
}

// "sub" of a JWT, or null.
const subjectOf = (jwt) => {
  try {
    const seg = String(jwt).split(".")[1];
    return JSON.parse(atob(seg.replace(/-/g, "+").replace(/_/g, "/"))).sub || null;
  } catch (_e) {
    return null;
  }
};

// Send this tab to Keycloak for a code. silent: prompt=none, which answers at once —
// a code when Keycloak has a session, else login_required.
export async function beginSignIn({ authority, clientId }, { silent = false } = {}) {
  const state = randomString(16);
  const verifier = randomString(48);
  // Without the request on record the answer can't be checked, and every return
  // would look like a fresh start: stop rather than loop.
  if (!writeJson(sessionStorage, PENDING_KEY, { state, verifier, authority, clientId, silent })) {
    throw new Error("This browser is not storing data for this site, so single sign-on can't finish.");
  }
  const params = new URLSearchParams({
    client_id: clientId,
    response_type: "code",
    scope: "openid",
    redirect_uri: `${window.location.origin}${CALLBACK_PATH}`,
    state,
    code_challenge: await codeChallengeOf(verifier),
    code_challenge_method: "S256",
  });
  if (silent) params.set("prompt", "none");
  window.location.assign(`${authority}/protocol/openid-connect/auth?${params}`);
}

// What /auth/callback's query means:
//   { kind: "tokens", tokens, session, claim } — Keycloak answered our request (claim:
//                                         the sign-in's, when this tab followed a link);
//   { kind: "ask" }                     — no answer to our request here (we came
//                                         from the session link): ask Keycloak;
//   { kind: "no-session" }              — prompt=none found no Keycloak session;
//   { kind: "other-user", session }     — Keycloak answered for someone other than
//                                         the user who just signed in (this browser
//                                         still held their session): end it;
//   { kind: "error", message }          — Keycloak refused, or the code didn't redeem.
export async function readCallback(search) {
  const query = new URLSearchParams(search);
  const pending = readJson(sessionStorage, PENDING_KEY);
  const state = query.get("state");
  if (!pending || !state || state !== pending.state) {
    // The session link's own redirect (no state of ours), or a stale tab.
    return { kind: "ask" };
  }
  writeJson(sessionStorage, PENDING_KEY, null);

  const error = query.get("error");
  if (error) {
    if (["login_required", "interaction_required", "consent_required"].includes(error)) {
      noteSilentMiss();
      return { kind: "no-session" };
    }
    return { kind: "error", message: query.get("error_description") || error };
  }

  try {
    const res = await tokenRequest(pending.authority, {
      grant_type: "authorization_code",
      client_id: pending.clientId,
      code: query.get("code") || "",
      redirect_uri: `${window.location.origin}${CALLBACK_PATH}`,
      code_verifier: pending.verifier,
    });
    const tokens = res.body || {};
    if (!res.ok || !tokens.access_token) {
      return { kind: "error", message: tokens.error_description || tokens.error || `Keycloak answered ${res.status}.` };
    }
    const session = { authority: pending.authority, clientId: pending.clientId, idToken: tokens.id_token || null };
    // The user who just signed in on F2's page, if this tab followed a session link.
    let expected = null;
    let claim = null;
    try {
      expected = sessionStorage.getItem(EXPECT_KEY);
      claim = sessionStorage.getItem(CLAIM_KEY);
      sessionStorage.removeItem(EXPECT_KEY);
      sessionStorage.removeItem(CLAIM_KEY);
    } catch (_e) {
      /* ignore */
    }
    if (expected && subjectOf(tokens.access_token) !== expected) {
      return { kind: "other-user", session };
    }
    return { kind: "tokens", tokens, session, claim };
  } catch (e) {
    return { kind: "error", message: e?.message || "Keycloak could not be reached." };
  }
}

// Renew with Keycloak. Resolves the new tokens; rejects with { response: { status } }
// when Keycloak refuses (400 invalid_grant: the session is over), or with a plain
// error when it can't be reached.
export async function refreshWithKeycloak(refreshToken, session = readSsoSession()) {
  const res = await tokenRequest(session.authority, {
    grant_type: "refresh_token",
    client_id: session.clientId,
    refresh_token: refreshToken,
  });
  if (!res.ok || !res.body?.access_token) {
    const refused = new Error(`Keycloak refused the renewal (${res.status}).`);
    refused.response = { status: res.status };
    throw refused;
  }
  if (res.body.id_token) storeSsoSession({ ...session, idToken: res.body.id_token });
  return res.body;
}

// At start-up: a page with no token tries Keycloak once (prompt=none). Resolves true
// when the tab is on its way to Keycloak, so the app shouldn't render.
export async function silentSignInOnStart({ hasToken }) {
  if (hasToken || typeof window === "undefined") return false;
  const { pathname, search } = window.location;
  if (NO_SILENT_SIGN_IN.test(pathname)) return false;
  // Just looked and found none: the sign-in page shows instead of a redirect loop.
  const missedAt = Number(sessionStorage.getItem(SILENT_MISS_KEY)) || 0;
  if (Date.now() - missedAt < SILENT_MISS_QUIET_MS) return false;

  // Last seen in password mode: render now, as before single sign-on; the answer
  // asked for here is what the next start goes by.
  const fresh = loadSignInMode();
  if (readJson(localStorage, MODE_KEY)?.mode === "password") return false;
  const mode = await fresh;
  if (mode.mode !== "oidc") return false;

  // Back to this page afterwards (pages/Login honours postLoginRedirect).
  if (pathname !== "/" && !/^\/(login|logout)(\/|\?|$)/.test(pathname)) {
    try {
      if (!sessionStorage.getItem("postLoginRedirect")) sessionStorage.setItem("postLoginRedirect", pathname + search);
    } catch (_e) {
      /* ignore */
    }
  }
  await beginSignIn(mode, { silent: true });
  return true;
}

// Test seam.
export function __resetForTests() {
  modeRequest = null;
}
